import React, { useState, useEffect } from "react"
import { getAPIBaseURL, getUser } from "../services/helpers"
import { updateData } from "../services/request"
import { getApiErrorMessage, getMessageFromResponseData } from "../services/apiErrorMessage"
import { validateEmail, validatePhoneNumber, validateOrganization, validateAddress, validateAccountNumber, validateAccountName, validateBank, validateCountry } from "../services/validators"
import { countries } from "../data"

const HospitalProfile = () => {
  const user = getUser()
  const [organization, setOrganization] = useState("")
  const [email, setEmail] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [country, setCountry] = useState("NG")
  const [address, setAddress] = useState("")
  const [accountNumber, setAccountNumber] = useState("")
  const [accountName, setAccountName] = useState("")
  const [bank, setBank] = useState("")
  const [btnState, setBtnState] = useState("Update Profile")
  const [error, setError] = useState([
    { field: "organization", msg: "" },
    { field: "email", msg: "" },
    { field: "phoneNumber", msg: "" },
    { field: "country", msg: "" },
    { field: "address", msg: "" },
    { field: "accountNumber", msg: "" },
    { field: "accountName", msg: "" },
    { field: "bank", msg: "" }
  ])
  const [genError, setGenError] = useState("")
  const [genMsg, setGenMsg] = useState("")
  const [loadingCountries] = useState(false)

  useEffect(() => {
    if (user) {
      setOrganization(user.organization || "")
      setEmail(user.email || "")
      setPhoneNumber(user.phoneNumber || "")
      setCountry(user.country || "NG")
      setAddress(user.address || "")
      setAccountNumber(user.accountNumber || "")
      setAccountName(user.accountName || "")
      setBank(user.bank || "")
    }
  }, [user])

  const validateForm = () => {
    let isValid = true
    const newErrors = [...error]

    const orgValidation = validateOrganization(organization)
    const orgErrorIndex = newErrors.findIndex(item => item.field == "organization")
    if (orgErrorIndex !== -1) {
      newErrors[orgErrorIndex].msg = orgValidation.result
      if (orgValidation.error != "") isValid = false
    }

    const emailValidation = validateEmail(email)
    const emailErrorIndex = newErrors.findIndex(item => item.field == "email")
    if (emailErrorIndex !== -1) {
      newErrors[emailErrorIndex].msg = emailValidation.result
      if (emailValidation.error != "") isValid = false
    }

    const phoneValidation = validatePhoneNumber(phoneNumber)
    const phoneErrorIndex = newErrors.findIndex(item => item.field == "phoneNumber")
    if (phoneErrorIndex !== -1) {
      newErrors[phoneErrorIndex].msg = phoneValidation.result
      if (phoneValidation.error != "") isValid = false
    }

    const countryValidation = validateCountry(country)
    const countryErrorIndex = newErrors.findIndex(item => item.field == "country")
    if (countryErrorIndex !== -1) {
      newErrors[countryErrorIndex].msg = countryValidation.result
      if (countryValidation.error != "") isValid = false
    }

    const addressValidation = validateAddress(address)
    const addressErrorIndex = newErrors.findIndex(item => item.field == "address")
    if (addressErrorIndex !== -1) {
      newErrors[addressErrorIndex].msg = addressValidation.result
      if (addressValidation.error != "") isValid = false
    }

    const accountNumberValidation = validateAccountNumber(accountNumber)
    const accountNumberIndex = newErrors.findIndex(item => item.field == "accountNumber")
    if (accountNumberIndex !== -1) {
      newErrors[accountNumberIndex].msg = accountNumberValidation.result
      if (accountNumberValidation.error != "") isValid = false
    }

    const accountNameValidation = validateAccountName(accountName)
    const accountNameIndex = newErrors.findIndex(item => item.field == "accountName")
    if (accountNameIndex !== -1) {
      newErrors[accountNameIndex].msg = accountNameValidation.result
      if (accountNameValidation.error != "") isValid = false
    }

    const bankValidation = validateBank(bank)
    const bankIndex = newErrors.findIndex(item => item.field == "bank")
    if (bankIndex !== -1) {
      newErrors[bankIndex].msg = bankValidation.result
      if (bankValidation.error != "") isValid = false
    }

    setError(newErrors)
    return isValid
  }

  const updateProfile = async () => {
    setGenError("")
    setGenMsg("")
    if (!validateForm()) {
      return
    }

    setBtnState("Updating...")
    const url = `${getAPIBaseURL()}/v1/users/update-account`
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
    const params = new URLSearchParams()
    params.append('organization', organization)
    params.append('email', email)
    params.append('phoneNumber', phoneNumber)
    params.append('country', country)
    params.append('address', address)
    params.append('accountNumber', accountNumber)
    params.append('accountName', accountName)
    params.append('bank', bank)

    const requestResult = await updateData(url, headers, params.toString())

    setBtnState("Update Profile")
    if (requestResult.error == "") {
      if (requestResult.result.status === 200) {
        setGenMsg(getMessageFromResponseData(requestResult.result.data) || 'Profile updated successfully')
        if (requestResult.result.data?.data) {
          localStorage.setItem('user', JSON.stringify(requestResult.result.data.data))
        }
      } else {
        setGenError(getApiErrorMessage(requestResult, 'Update failed'))
      }
    } else {
      setGenError(getApiErrorMessage(requestResult, 'Update failed'))
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-3 sm:px-0">
      <div className="rounded-lg bg-white shadow-sm border border-gray-200">
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-4 sm:px-6 sm:py-6">
          <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">Hospital Profile</h2>
          <p className="mt-1 text-sm text-gray-600">Update your hospital information and settings</p>
        </div>

        <div className="p-4 sm:p-8">
          <div className="space-y-6">
            {genMsg ? (
              <div className="rounded-lg bg-green-50 border border-green-200 p-4 text-sm font-medium text-green-700">
                {genMsg}
              </div>
            ) : null}
            {genError ? (
              <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm font-medium text-red-700">
                {genError}
              </div>
            ) : null}

            <div>
              <label htmlFor="organization" className="mb-2 block text-sm font-semibold text-gray-900">Hospital/Organization Name</label>
              <input
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
                type="text"
                id="organization"
                className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-0"
                placeholder="St. Mary's Hospital"
              />
              {error.find(item => item.field == "organization")?.msg ? (
                <p className="mt-1.5 text-sm font-medium text-red-600">{error.find(item => item.field == "organization").msg}</p>
              ) : null}
            </div>

            <div>
              <label htmlFor="email" className="mb-2 block text-sm font-semibold text-gray-900">Email Address</label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                id="email"
                className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-0"
                placeholder="contact@hospital.com"
              />
              {error.find(item => item.field == "email")?.msg ? (
                <p className="mt-1.5 text-sm font-medium text-red-600">{error.find(item => item.field == "email").msg}</p>
              ) : null}
            </div>

            <div>
              <label htmlFor="phoneNumber" className="mb-2 block text-sm font-semibold text-gray-900">Phone Number</label>
              <input
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                type="tel"
                id="phoneNumber"
                className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-0"
                placeholder="08012345678"
              />
              {error.find(item => item.field == "phoneNumber")?.msg ? (
                <p className="mt-1.5 text-sm font-medium text-red-600">{error.find(item => item.field == "phoneNumber").msg}</p>
              ) : null}
            </div>

            <div>
              <label htmlFor="country" className="mb-2 block text-sm font-semibold text-gray-900">Country</label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                id="country"
                className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-0"
              >
                <option value="">{loadingCountries ? 'Loading countries...' : 'Select Country'}</option>
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
              {error.find(item => item.field == "country")?.msg ? (
                <p className="mt-1.5 text-sm font-medium text-red-600">{error.find(item => item.field == "country").msg}</p>
              ) : null}
            </div>

            <div>
              <label htmlFor="address" className="mb-2 block text-sm font-semibold text-gray-900">Address</label>
              <textarea
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                id="address"
                rows="4"
                className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-0 resize-none"
                placeholder="Enter hospital full address"
              />
              {error.find(item => item.field == "address")?.msg ? (
                <p className="mt-1.5 text-sm font-medium text-red-600">{error.find(item => item.field == "address").msg}</p>
              ) : null}
            </div>

            <div>
              <label htmlFor="accountNumber" className="mb-2 block text-sm font-semibold text-gray-900">Account Number</label>
              <input
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                type="text"
                id="accountNumber"
                className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-0"
                placeholder="0123456789"
              />
              {error.find(item => item.field == "accountNumber")?.msg ? (
                <p className="mt-1.5 text-sm font-medium text-red-600">{error.find(item => item.field == "accountNumber").msg}</p>
              ) : null}
            </div>

            <div>
              <label htmlFor="accountName" className="mb-2 block text-sm font-semibold text-gray-900">Account Name</label>
              <input
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                type="text"
                id="accountName"
                className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-0"
                placeholder="St. Mary's Hospital"
              />
              {error.find(item => item.field == "accountName")?.msg ? (
                <p className="mt-1.5 text-sm font-medium text-red-600">{error.find(item => item.field == "accountName").msg}</p>
              ) : null}
            </div>

            <div>
              <label htmlFor="bank" className="mb-2 block text-sm font-semibold text-gray-900">Bank</label>
              <input
                value={bank}
                onChange={(e) => setBank(e.target.value)}
                type="text"
                id="bank"
                className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-0"
                placeholder="Access Bank"
              />
              {error.find(item => item.field == "bank")?.msg ? (
                <p className="mt-1.5 text-sm font-medium text-red-600">{error.find(item => item.field == "bank").msg}</p>
              ) : null}
            </div>

            <div className="pt-2">
              <button
                onClick={updateProfile}
                type="button"
                className="w-full rounded-lg bg-black px-6 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
              >
                {btnState}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HospitalProfile

